(() => {
  if (window.MoltenCartV1?.initialized) return;

  const state = {
    initialized: true,
    pending: null,
    observedErrors: new WeakSet(),
    observedGiftnoteButtons: new WeakSet(),
    observedGiftnoteCores: new WeakSet(),
    observedGiftnoteActions: new WeakSet(),
    giftnoteRefreshFrame: null,
    pendingFreshGiftCard: false,
    awaitingGiftnoteSave: false,
    pendingGiftnoteSummary: null,
    freshFieldsResetFor: '',
  };

  window.MoltenCartV1 = state;

  const itemSelector = '[data-cart-item]';
  const quantitySelector = '[data-quantity-field]';
  const removeSelector = '[data-item-remove]';
  const giftCardOnlySelector = '[data-digital-gift-card-only="true"]';
  const digitalGiftCardItemSelector = '[data-cart-item].cart__item--digital-gift-card';
  const giftCardPendingStorageKey = 'molten:gift-card-details-required';
  const giftCardSavedStorageKey = 'molten:gift-card-details-saved';
  const giftCardSummaryStorageKey = 'molten:gift-card-delivery-summary';

  const tokenise = (template, values) => {
    let result = template || '';
    Object.entries(values).forEach(([key, value]) => {
      result = result.replaceAll(`||${key}||`, value ?? '');
    });
    return result;
  };

  const itemDetails = (element) => {
    const item = element?.closest(itemSelector);
    return {
      item,
      key: item?.dataset.item || '',
      title: item?.dataset.itemTitle || '',
    };
  };

  const focusDescriptor = (element, item) => {
    if (!element || !item) return null;
    if (element.matches('[data-quantity-minus]')) return {key: item.dataset.item, selector: '[data-quantity-minus]'};
    if (element.matches('[data-quantity-plus]')) return {key: item.dataset.item, selector: '[data-quantity-plus]'};
    if (element.matches(quantitySelector)) return {key: item.dataset.item, selector: quantitySelector};
    if (element.matches(removeSelector)) {
      const nextItem = item.nextElementSibling?.matches(itemSelector) ? item.nextElementSibling : null;
      const previousItem = item.previousElementSibling?.matches(itemSelector) ? item.previousElementSibling : null;
      const destination = nextItem || previousItem;
      return destination ? {key: destination.dataset.item, selector: quantitySelector} : {heading: true};
    }
    return null;
  };

  const setPending = (type, element) => {
    const {item, key, title} = itemDetails(element);
    if (!item || !key) return;
    state.pending = {
      type,
      key,
      title,
      focus: focusDescriptor(element, item),
    };
  };

  const onDocumentClick = (event) => {
    const giftCardAdd = event.target.closest('[data-gift-add]');
    if (giftCardAdd && cartCount() === 0) {
      state.pendingFreshGiftCard = true;
    }

    const remove = event.target.closest(removeSelector);
    if (remove) {
      setPending('remove', remove);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-button]');
    if (quantityButton && !quantityButton.disabled && !quantityButton.closest('[data-atelier-cart-quantity]')) {
      setPending('quantity', quantityButton);
    }
  };

  const onDocumentChange = (event) => {
    if (!event.target.matches(quantitySelector)) return;
    if (event.target.readOnly || event.target.closest('[data-atelier-cart-quantity]')) return;
    setPending('quantity', event.target);
  };

  const cartCount = () => {
    const fields = document.querySelectorAll('[data-items-holder] [data-quantity-field]');
    if (fields.length) {
      return Array.from(fields).reduce((sum, field) => sum + (Number.parseInt(field.value, 10) || 0), 0);
    }

    const initialCount = document.querySelector('[data-molten-cart-utility]')?.dataset.cartInitialCount;
    return Number.parseInt(initialCount, 10) || 0;
  };

  const syncCounts = () => {
    const count = cartCount();

    document.querySelectorAll('[data-molten-cart-count]').forEach((node) => {
      const template = count === 1 ? node.dataset.countOne : node.dataset.countOther;
      node.textContent = tokenise(template, {count: String(count)});
      node.classList.toggle('hidden', count === 0);
      node.hidden = count === 0;
      if (count === 0) {
        node.setAttribute('aria-hidden', 'true');
        node.style.setProperty('display', 'none');
      } else {
        node.removeAttribute('aria-hidden');
        node.style.removeProperty('display');
      }
    });

    document.querySelectorAll('[data-molten-cart-header-count]').forEach((node) => {
      node.textContent = count > 0 ? ` (${count})` : '';
    });

    document.querySelectorAll('[data-molten-cart-utility]').forEach((node) => {
      const template = count === 1 ? node.dataset.cartLabelOne : node.dataset.cartLabelOther;
      if (template) node.setAttribute('aria-label', tokenise(template, {count: String(count)}));
      node.classList.toggle('cart__toggle--has-items', count > 0);
    });

    return count;
  };

  const syncDrawerState = () => {
    const count = syncCounts();

    document.querySelectorAll('[data-molten-cart-heading-text]').forEach((node) => {
      node.textContent = count === 0 ? node.dataset.emptyLabel : node.dataset.populatedLabel;
    });

    return count;
  };

  const currentSubtotal = () => {
    const totals = Array.from(document.querySelectorAll('[data-cart-total-price]'));
    const visible = totals.find((node) => node.offsetParent !== null) || totals[0];
    return visible?.textContent.trim() || '';
  };

  const statusRegion = () => {
    const regions = Array.from(document.querySelectorAll('[data-molten-cart-status]'));
    return regions.find((node) => node.offsetParent !== null) || regions[0] || null;
  };

  const restoreFocus = (descriptor) => {
    if (!descriptor) return;
    let destination = null;

    if (descriptor.key) {
      const escapedKey = window.CSS?.escape ? window.CSS.escape(descriptor.key) : descriptor.key.replaceAll('"', '\\"');
      destination = document.querySelector(`[data-item="${escapedKey}"] ${descriptor.selector}`);
    }

    if (!destination && descriptor.heading) destination = document.querySelector('[data-molten-cart-heading]');
    if (!destination) destination = document.querySelector('[data-molten-cart-heading]');
    destination?.focus({preventScroll: true});
  };

  const announceSuccess = () => {
    const pending = state.pending;
    if (!pending) {
      syncDrawerState();
      const region = statusRegion();
      if (region) region.textContent = '';
      return;
    }

    if (pending.type === 'remove' && !document.querySelector('[data-items-holder] [data-quantity-field]')) {
      document.querySelectorAll('[data-molten-cart-utility]').forEach((node) => {
        node.dataset.cartInitialCount = '0';
      });
    }

    syncDrawerState();
    const region = statusRegion();
    const template = pending.type === 'remove' ? region?.dataset.removedTemplate : region?.dataset.quantityTemplate;
    const message = tokenise(template, {
      product_title: pending.title,
      subtotal: currentSubtotal(),
    });

    if (region && message) {
      region.textContent = '';
      requestAnimationFrame(() => {
        region.textContent = message;
      });
    }

    requestAnimationFrame(() => restoreFocus(pending.focus));
    state.pending = null;
  };

  const applyErrorCopy = (container) => {
    if (!container.classList.contains('is-expanded') || state.pending?.type !== 'quantity') return;
    const content = container.querySelector('[data-error-message]');
    const template = container.dataset.moltenErrorTemplate;
    if (content && template) content.textContent = tokenise(template, {product_title: state.pending.title});
    state.pending = null;
  };

  const bindErrorObservers = () => {
    document.querySelectorAll('[data-cart-errors]').forEach((container) => {
      if (state.observedErrors.has(container)) return;
      const observer = new MutationObserver(() => applyErrorCopy(container));
      observer.observe(container, {attributes: true, attributeFilter: ['class'], childList: true, subtree: true});
      state.observedErrors.add(container);
    });
  };

  const cartRoots = () => Array.from(document.querySelectorAll('.cart-drawer.molten-cart-v1, .cart.molten-cart-v1'));

  const cartItemsForRoot = (root) => Array.from(root?.querySelectorAll('[data-items-holder] [data-cart-item]') || []);

  const rootIsDigitalGiftCardOnly = (root) => {
    const items = cartItemsForRoot(root);
    return items.length > 0 && items.every((item) => item.matches(digitalGiftCardItemSelector));
  };

  const digitalGiftCardOnly = () => {
    const populatedRoots = cartRoots().filter((root) => cartItemsForRoot(root).length > 0);
    if (populatedRoots.length) return populatedRoots.some(rootIsDigitalGiftCardOnly);
    return Boolean(document.querySelector(giftCardOnlySelector));
  };

  const currentGiftCardItems = () => {
    const visibleRoot = cartRoots().find((root) => root.offsetParent !== null && rootIsDigitalGiftCardOnly(root));
    const root = visibleRoot || cartRoots().find(rootIsDigitalGiftCardOnly);
    return root ? cartItemsForRoot(root).filter((item) => item.matches(digitalGiftCardItemSelector)) : [];
  };

  const currentGiftCardKey = () => {
    const item = currentGiftCardItems()[0];
    return item?.dataset.giftCardVariantId || item?.dataset.item || '';
  };

  const readPendingGiftCardKey = () => {
    try {
      return window.localStorage.getItem(giftCardPendingStorageKey) || '';
    } catch (error) {
      return '';
    }
  };

  const writePendingGiftCardKey = (key) => {
    try {
      if (key) window.localStorage.setItem(giftCardPendingStorageKey, key);
    } catch (error) {
      // The line-item state remains the fallback when storage is unavailable.
    }
  };

  const clearPendingGiftCardKey = () => {
    try {
      window.localStorage.removeItem(giftCardPendingStorageKey);
    } catch (error) {
      // No action is needed when storage is unavailable.
    }
  };

  const readSavedGiftCardKey = () => {
    try {
      return window.localStorage.getItem(giftCardSavedStorageKey) || '';
    } catch (error) {
      return '';
    }
  };

  const writeSavedGiftCardKey = (key) => {
    try {
      if (key) window.localStorage.setItem(giftCardSavedStorageKey, key);
    } catch (error) {
      // The live Giftnote state remains the fallback when storage is unavailable.
    }
  };

  const clearSavedGiftCardKey = () => {
    try {
      window.localStorage.removeItem(giftCardSavedStorageKey);
    } catch (error) {
      // No action is needed when storage is unavailable.
    }
  };

  const readGiftCardSummary = () => {
    const key = currentGiftCardKey();
    if (!key) return null;

    try {
      const summary = JSON.parse(window.localStorage.getItem(giftCardSummaryStorageKey) || 'null');
      return summary?.key === key ? summary : null;
    } catch (error) {
      return null;
    }
  };

  const writeGiftCardSummary = (summary) => {
    if (!summary?.key) return;
    try {
      window.localStorage.setItem(giftCardSummaryStorageKey, JSON.stringify(summary));
    } catch (error) {
      // The fallback saved-state copy remains available when storage is unavailable.
    }
  };

  const clearGiftCardSummary = () => {
    try {
      window.localStorage.removeItem(giftCardSummaryStorageKey);
    } catch (error) {
      // No action is needed when storage is unavailable.
    }
  };

  const currentGiftCardNeedsDetails = () => {
    const key = currentGiftCardKey();
    if (!key) return false;
    const explicitlyPending = readPendingGiftCardKey() === key;
    const lineItemHasDetails = currentGiftCardItems().every((item) => item.dataset.giftnoteDetailsSaved === 'true');
    const confirmedInThisCart = readSavedGiftCardKey() === key;
    return explicitlyPending || (!lineItemHasDetails && !confirmedInThisCart);
  };

  const currentGiftCardHasSavedDetails = () => {
    const items = currentGiftCardItems();
    const key = currentGiftCardKey();
    return items.length > 0 && (
      items.every((item) => item.dataset.giftnoteDetailsSaved === 'true') ||
      Boolean(key && readSavedGiftCardKey() === key)
    );
  };

  const setText = (node, text) => {
    if (node && node.textContent !== text) node.textContent = text;
  };

  const rememberText = (node) => {
    if (node && node.dataset.moltenDefaultText === undefined) {
      node.dataset.moltenDefaultText = node.textContent.trim();
    }
  };

  const rememberHtml = (node) => {
    if (node && node.dataset.moltenDefaultHtml === undefined) {
      node.dataset.moltenDefaultHtml = node.innerHTML;
    }
  };

  const rememberPlaceholder = (node) => {
    if (node && node.dataset.moltenDefaultPlaceholder === undefined) {
      node.dataset.moltenDefaultPlaceholder = node.getAttribute('placeholder') || '';
    }
  };

  const setRequiredState = (field, required) => {
    if (!field) return;
    if (field.dataset.moltenDefaultRequired === undefined) {
      field.dataset.moltenDefaultRequired = field.hasAttribute('required') ? 'true' : 'false';
    }

    const shouldBeRequired = required || field.dataset.moltenDefaultRequired === 'true';
    field.toggleAttribute('required', shouldBeRequired);
    if (required) {
      field.setAttribute('aria-required', 'true');
    } else {
      field.removeAttribute('aria-required');
    }
  };

  const toggleClass = (node, className, enabled) => {
    if (!node || node.classList.contains(className) === enabled) return;
    node.classList.toggle(className, enabled);
  };

  const giftnoteRequiredFields = [
    {field: '#to', labels: ['label[for="to"]']},
    {field: '#from', labels: ['label[for="from"]']},
    {field: '#message', labels: ['label[for="message"]']},
    {field: '#email', labels: ['label[for="email"]', 'label[for="email-field"]']},
  ];

  const setGiftnoteRequiredFields = (root, required) => {
    giftnoteRequiredFields.forEach(({field, labels}) => {
      labels.forEach((selector) => {
        toggleClass(root.querySelector(selector), 'molten-required-label', required);
      });
      setRequiredState(root.querySelector(field), required);
    });
  };

  const giftnoteButtonStyles = `
    :host(.molten-gift-card-button) {
      display: block !important;
      width: 100% !important;
    }
    :host(.molten-gift-card-button) #container {
      width: 100% !important;
      justify-content: flex-start !important;
    }
    :host(.molten-gift-card-button) #button {
      display: inline-flex !important;
      width: 100% !important;
      min-width: 44px !important;
      min-height: 48px !important;
      padding: 13px 20px !important;
      margin: 0 !important;
      align-items: center !important;
      justify-content: center !important;
      border: 1px solid #6b4a41 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-sizing: border-box !important;
      color: #5d3c34 !important;
      font-family: "Molten Cart Libre Franklin", system-ui, sans-serif !important;
      font-size: 14px !important;
      font-style: normal !important;
      font-weight: 500 !important;
      line-height: 1.4 !important;
      letter-spacing: 0 !important;
      text-align: center !important;
      text-decoration: none !important;
      text-transform: none !important;
      cursor: pointer !important;
    }
    :host(.molten-gift-card-button) #button:hover {
      background: #f2eee8 !important;
    }
    :host(.molten-gift-card-button) #button:focus-visible {
      outline: 2px solid #6b4a41 !important;
      outline-offset: 2px !important;
    }
    :host(.molten-gift-card-button:not(.molten-giftnote-saved)) #add {
      display: inline !important;
    }
    :host(.molten-gift-card-button:not(.molten-giftnote-saved)) #edit {
      display: none !important;
    }
    :host(.molten-gift-card-button.molten-giftnote-saved) #add {
      display: none !important;
    }
    :host(.molten-gift-card-button.molten-giftnote-saved) #edit {
      display: inline !important;
    }
    :host(.molten-gift-card-button.molten-giftnote-saved) #container {
      justify-content: flex-start !important;
    }
    :host(.molten-gift-card-button.molten-giftnote-saved) #button {
      width: auto !important;
      min-height: 44px !important;
      padding: 10px 0 !important;
      border: 0 !important;
      background: transparent !important;
      color: #5d3c34 !important;
      font-size: 13px !important;
      font-weight: 400 !important;
      justify-content: flex-start !important;
      text-align: left !important;
      text-decoration: underline !important;
      text-underline-offset: 3px !important;
    }
  `;

  const prepareGiftnoteButton = (button, giftCardMode) => {
    if (!button) return;

    if (button.dataset.moltenDefaultAdd === undefined) button.dataset.moltenDefaultAdd = button.getAttribute('data-add') || '';
    if (button.dataset.moltenDefaultEdit === undefined) button.dataset.moltenDefaultEdit = button.getAttribute('data-edit') || '';

    toggleClass(button, 'molten-gift-card-button', giftCardMode);

    if (giftCardMode) {
      button.setAttribute('data-add', 'Personalise this gift card');
      button.setAttribute('data-edit', 'Edit gift card details');
    } else {
      button.setAttribute('data-add', button.dataset.moltenDefaultAdd);
      button.setAttribute('data-edit', button.dataset.moltenDefaultEdit);
    }

    if (!button.shadowRoot) return;

    let style = button.shadowRoot.querySelector('#molten-giftnote-button-styles');
    if (!style) {
      style = document.createElement('style');
      style.id = 'molten-giftnote-button-styles';
      style.textContent = giftnoteButtonStyles;
      button.shadowRoot.append(style);
    }

    if (giftCardMode) {
      setText(button.shadowRoot.querySelector('#add'), 'Personalise this gift card');
      setText(button.shadowRoot.querySelector('#edit'), 'Edit gift card details');
    }
  };

  const syncGiftCardMode = () => {
    cartRoots().forEach((root) => {
      const giftCardMode = rootIsDigitalGiftCardOnly(root);
      const form = root.querySelector('[data-cart-form]');
      const button = root.querySelector('giftnote-button');
      const widget = button?.closest('[data-cart-widget]');
      const guidance = widget?.querySelector('[data-giftnote-guidance]');
      const status = widget?.querySelector('[data-giftnote-status]');
      const delivery = root.querySelector('.cart__text');

      toggleClass(root, 'molten-cart-v1--gift-card-only', giftCardMode);
      toggleClass(widget, 'cart__widget--gift-card-personalisation', giftCardMode);
      if (widget) widget.hidden = !giftCardMode;
      if (form) form.dataset.digitalGiftCardOnly = String(giftCardMode);
      if (guidance && !giftCardMode) guidance.hidden = true;
      if (status && !giftCardMode) status.hidden = true;

      if (delivery) {
        if (delivery.dataset.moltenDefaultText === undefined) delivery.dataset.moltenDefaultText = delivery.textContent.trim();
        setText(delivery, giftCardMode ? 'Delivered by email. No shipping required.' : delivery.dataset.moltenDefaultText);
        toggleClass(delivery, 'cart__text--digital-delivery', giftCardMode);
      }

      prepareGiftnoteButton(button, giftCardMode);
    });
  };

  const syncGiftnoteButtonState = (button) => {
    const widget = button.closest('[data-giftnote-widget]');
    if (!widget || !button.shadowRoot) return;

    const giftCardMode = rootIsDigitalGiftCardOnly(button.closest('.cart-drawer, .cart'));
    if (!giftCardMode) return;

    const isSaved = currentGiftCardHasSavedDetails() && !currentGiftCardNeedsDetails();
    const guidance = widget.querySelector('[data-giftnote-guidance]');
    const status = widget.querySelector('[data-giftnote-status]');
    const statusPrimary = status?.querySelector('[data-giftnote-status-primary]');
    const statusSecondary = status?.querySelector('[data-giftnote-status-secondary]');
    const summary = readGiftCardSummary();
    const hasRecipient = Boolean(summary?.recipient);
    const hasDelivery = summary?.deliveryMethod === 'now' || (
      summary?.deliveryMethod === 'later' && summary?.time && summary?.date
    );

    toggleClass(widget, 'is-saved', isSaved);
    toggleClass(button, 'molten-giftnote-saved', isSaved);
    if (guidance) guidance.hidden = isSaved;
    if (status) status.hidden = !isSaved;
    if (isSaved && hasRecipient && hasDelivery) {
      setText(statusPrimary, `For ${summary.recipient}`);
      setText(
        statusSecondary,
        summary.deliveryMethod === 'later'
          ? `Scheduled for ${formatGiftCardTime(summary.time)} on ${formatGiftCardDate(summary.date)}`
          : 'Sent by email after checkout'
      );
      if (statusSecondary) statusSecondary.hidden = false;
    } else if (isSaved) {
      setText(statusPrimary, 'Gift card details saved');
      setText(statusSecondary, '');
      if (statusSecondary) statusSecondary.hidden = true;
    }
    setText(button.shadowRoot.querySelector('#add'), 'Personalise this gift card');
    setText(button.shadowRoot.querySelector('#edit'), 'Edit gift card details');
  };

  const bindGiftnoteButtonObservers = () => {
    document.querySelectorAll('giftnote-button').forEach((button) => {
      if (!button.shadowRoot) return;
      prepareGiftnoteButton(button, rootIsDigitalGiftCardOnly(button.closest('.cart-drawer, .cart')));
      syncGiftnoteButtonState(button);
      if (state.observedGiftnoteButtons.has(button)) return;

      const observer = new MutationObserver(() => syncGiftnoteButtonState(button));
      observer.observe(button.shadowRoot, {attributes: true, attributeFilter: ['class'], childList: true, subtree: true});
      state.observedGiftnoteButtons.add(button);
    });
  };

  const giftnoteGiftCardStyles = `
    #giftnote-modal.molten-gift-card-mode {
      padding: 24px !important;
      color: #5d3c34 !important;
      font-family: "Molten Cart Libre Franklin", system-ui, sans-serif !important;
    }
    #giftnote-modal.molten-gift-card-mode #backdrop {
      background: rgba(52, 42, 41, 0.52) !important;
    }
    #giftnote-modal.molten-gift-card-mode #content {
      width: min(100%, 608px) !important;
      max-width: 608px !important;
      height: auto !important;
      max-height: calc(100dvh - 48px) !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: #fbf8f4 !important;
      box-shadow: 0 18px 54px rgba(52, 42, 41, 0.16) !important;
    }
    #giftnote-modal.molten-gift-card-mode:focus,
    #giftnote-modal.molten-gift-card-mode:focus-visible,
    #giftnote-modal.molten-gift-card-mode #content:focus,
    #giftnote-modal.molten-gift-card-mode #content:focus-visible,
    #giftnote-modal.molten-gift-card-mode #content > div:focus,
    #giftnote-modal.molten-gift-card-mode #content > div:focus-visible {
      outline: none !important;
    }
    #giftnote-modal.molten-gift-card-mode #content > div {
      padding: 28px 30px 26px !important;
      border: 1px solid #d8cec5 !important;
      border-radius: 0 !important;
      background: #fbf8f4 !important;
      box-shadow: none !important;
    }
    #giftnote-modal.molten-gift-card-mode #content > div > .flex {
      align-items: flex-start !important;
    }
    #giftnote-modal.molten-gift-card-mode #giftnote-modal-title {
      width: 100% !important;
      max-width: none !important;
      padding-right: 150px !important;
      margin: 0 0 6px !important;
      color: #5d3c34 !important;
      font-size: 21px !important;
      font-weight: 500 !important;
      line-height: 1.28 !important;
      letter-spacing: 0 !important;
      text-align: left !important;
    }
    #giftnote-modal.molten-gift-card-mode #giftnote-modal-description {
      width: 100% !important;
      max-width: 47ch !important;
      margin: 0 0 18px !important;
      color: #5d3c34 !important;
      font-size: 15px !important;
      font-weight: 400 !important;
      line-height: 1.55 !important;
      text-align: left !important;
    }
    #giftnote-modal.molten-gift-card-mode #giftnote-modal-description p {
      margin: 0 !important;
    }
    #giftnote-modal.molten-gift-card-mode #close-button {
      top: 16px !important;
      right: 18px !important;
      width: 40px !important;
      height: 40px !important;
      padding: 0 !important;
      display: grid !important;
      place-items: center !important;
      border: 1px solid #c9c0b7 !important;
      border-radius: 50% !important;
      background: transparent !important;
      color: #5d3c34 !important;
    }
    #giftnote-modal.molten-gift-card-mode #close-button svg {
      width: 13px !important;
      height: 13px !important;
    }
    #giftnote-modal.molten-gift-card-mode #close-button:hover {
      background: #f2eee8 !important;
    }
    #giftnote-modal.molten-gift-card-mode #remove-button {
      top: 16px !important;
      right: 70px !important;
      left: auto !important;
      padding: 11px 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      color: #7a5c52 !important;
      font-size: 13px !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
      text-decoration: underline !important;
      text-underline-offset: 3px !important;
    }
    #giftnote-modal.molten-gift-card-mode.molten-fresh-gift-card #remove-button {
      display: none !important;
    }
    #giftnote-modal.molten-gift-card-mode #form {
      padding-top: 18px !important;
      border-top: 1px solid #ded6ce !important;
    }
    #giftnote-modal.molten-gift-card-mode #form > .grid {
      gap: 10px !important;
      margin-bottom: 20px !important;
    }
    #giftnote-modal.molten-gift-card-mode #tracked-tab-parent,
    #giftnote-modal.molten-gift-card-mode #tracked-panel,
    #giftnote-modal.molten-gift-card-mode .molten-gift-card-phone {
      display: none !important;
    }
    #giftnote-modal.molten-gift-card-mode #method {
      flex-wrap: nowrap !important;
    }
    #giftnote-modal.molten-gift-card-mode #method > li {
      width: 50% !important;
    }
    #giftnote-modal.molten-gift-card-mode .molten-gift-card-email {
      grid-column: 1 / -1 !important;
    }
    #giftnote-modal.molten-gift-card-mode,
    #giftnote-modal.molten-gift-card-mode #to-field,
    #giftnote-modal.molten-gift-card-mode #from-field,
    #giftnote-modal.molten-gift-card-mode #message-field,
    #giftnote-modal.molten-gift-card-mode #email-field,
    #giftnote-modal.molten-gift-card-mode #method,
    #giftnote-modal.molten-gift-card-mode #method button,
    #giftnote-modal.molten-gift-card-mode #instant-panel,
    #giftnote-modal.molten-gift-card-mode #scheduled-panel,
    #giftnote-modal.molten-gift-card-mode #save-button {
      border-radius: 0 !important;
    }
    #giftnote-modal.molten-gift-card-mode #to-field,
    #giftnote-modal.molten-gift-card-mode #from-field,
    #giftnote-modal.molten-gift-card-mode #message-field,
    #giftnote-modal.molten-gift-card-mode #email-field,
    #giftnote-modal.molten-gift-card-mode #date-field,
    #giftnote-modal.molten-gift-card-mode #time-field {
      min-height: 64px !important;
      padding: 8px 11px !important;
      border: 1px solid #c9c0b7 !important;
      background: #fbf8f4 !important;
      box-shadow: none !important;
      color: #5d3c34 !important;
    }
    #giftnote-modal.molten-gift-card-mode #message-field {
      min-height: 104px !important;
    }
    #giftnote-modal.molten-gift-card-mode :where(#to-field, #from-field, #message-field, #email-field, #date-field, #time-field):focus-within {
      border-color: #6b4a41 !important;
      box-shadow: inset 0 0 0 1px #6b4a41 !important;
    }
    #giftnote-modal.molten-gift-card-mode :where(#to-field, #from-field, #message-field, #email-field, #date-field, #time-field) label {
      margin: 0 0 4px !important;
      color: #5d3c34 !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      line-height: 1.4 !important;
      letter-spacing: 0 !important;
    }
    #giftnote-modal.molten-gift-card-mode :where(#to, #from, #message, #email, #date, #time) {
      background: transparent !important;
      color: #5d3c34 !important;
      font-family: "Molten Cart Libre Franklin", system-ui, sans-serif !important;
      font-size: 16px !important;
      font-weight: 400 !important;
      line-height: 1.45 !important;
    }
    #giftnote-modal.molten-gift-card-mode :where(#to, #from, #message, #email)::placeholder {
      color: #7a5c52 !important;
      opacity: 1 !important;
    }
    #giftnote-modal.molten-gift-card-mode #message-count {
      color: #7a5c52 !important;
      font-size: 13px !important;
      line-height: 1.5 !important;
    }
    #giftnote-modal.molten-gift-card-mode h3 {
      margin: 0 !important;
      color: #5d3c34 !important;
      font-family: "Molten Cart Libre Franklin", system-ui, sans-serif !important;
      font-size: 16px !important;
      font-weight: 500 !important;
      line-height: 1.4 !important;
      letter-spacing: 0 !important;
    }
    #giftnote-modal.molten-gift-card-mode #method {
      min-height: 46px !important;
      border: 1px solid #c9c0b7 !important;
      background: transparent !important;
    }
    #giftnote-modal.molten-gift-card-mode #methods {
      margin-top: 8px !important;
      padding: 8px 0 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }
    #giftnote-modal.molten-gift-card-mode #method > li + li {
      border-left: 1px solid #c9c0b7 !important;
    }
    #giftnote-modal.molten-gift-card-mode #method button {
      min-height: 46px !important;
      padding: 11px 14px !important;
      color: #7a5c52 !important;
      font-family: "Molten Cart Libre Franklin", system-ui, sans-serif !important;
      font-size: 15px !important;
      font-weight: 400 !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
    }
    #giftnote-modal.molten-gift-card-mode #method button[aria-selected="true"] {
      background: #f2eee8 !important;
      color: #5d3c34 !important;
      box-shadow: inset 0 -2px 0 #6b4a41 !important;
    }
    #giftnote-modal.molten-gift-card-mode :where(#instant-panel, #scheduled-panel) {
      margin-top: 0 !important;
      padding: 12px 0 16px !important;
      border: 0 !important;
      background: transparent !important;
      color: #7a5c52 !important;
      font-size: 13.5px !important;
      line-height: 1.58 !important;
    }
    #giftnote-modal.molten-gift-card-mode #save-button {
      min-height: 48px !important;
      padding: 13px 20px !important;
      border: 1px solid #6b4a41 !important;
      background: #6b4a41 !important;
      color: #fdf5ee !important;
      font-family: "Molten Cart Libre Franklin", system-ui, sans-serif !important;
      font-size: 15px !important;
      font-weight: 500 !important;
      line-height: 1.4 !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
    }
    #giftnote-modal.molten-gift-card-mode #save-button:hover {
      background: #5d3c34 !important;
    }
    #giftnote-modal.molten-gift-card-mode label.molten-required-label::after {
      content: "Required";
      float: right;
      margin-left: 12px;
      color: #7a5c52;
      font-size: 13px;
      font-weight: 400;
      line-height: 1.5;
      letter-spacing: 0;
    }
    #giftnote-modal.molten-gift-card-mode #message-field label.molten-required-label::after {
      margin-right: 44px;
    }
    #powered,
    a[href*="giftnote.com"] {
      display: none !important;
    }
    #alert-save.molten-gift-card-save-alert {
      display: none !important;
    }
    @media (max-width: 639px) {
      #giftnote-modal.molten-gift-card-mode {
        padding: 0 !important;
      }
      #giftnote-modal.molten-gift-card-mode #content {
        width: 100% !important;
        max-width: none !important;
        height: 100dvh !important;
        max-height: 100dvh !important;
      }
      #giftnote-modal.molten-gift-card-mode #content > div {
        min-height: 100dvh !important;
        padding: 22px 18px 26px !important;
        border: 0 !important;
      }
      #giftnote-modal.molten-gift-card-mode #giftnote-modal-title {
        max-width: 15ch !important;
        padding-right: 54px !important;
        font-size: 20px !important;
      }
      #giftnote-modal.molten-gift-card-mode #close-button {
        top: 14px !important;
        right: 14px !important;
      }
      #giftnote-modal.molten-gift-card-mode #remove-button {
        position: static !important;
        order: 10 !important;
        margin: 12px 0 0 !important;
        align-self: flex-start !important;
      }
      #giftnote-modal.molten-gift-card-mode #method {
        flex-direction: row !important;
      }
    }
  `;

  const giftnoteModalIsOpen = (modal) => Boolean(
    modal && (modal.getAttribute('aria-hidden') === 'false' || !modal.classList.contains('hidden'))
  );

  const formatGiftCardDate = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    let day;
    let month;
    let year;
    let match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
      [, year, month, day] = match;
    } else {
      match = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      if (match) [, day, month, year] = match;
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    const monthName = monthNames[Number.parseInt(month, 10) - 1];
    if (!day || !monthName || !year) return raw;
    return `${Number.parseInt(day, 10)} ${monthName} ${year}`;
  };

  const formatGiftCardTime = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/\s+(AM|PM)$/i.test(raw)) {
      return raw.replace(/\s+(AM|PM)$/i, (_, meridiem) => ` ${meridiem.toLowerCase()}`);
    }

    const match = raw.match(/^(\d{1,2}):?(\d{2})$/);
    if (!match) return raw;
    const hour = Number.parseInt(match[1], 10);
    const minute = match[2];
    if (hour > 23 || Number.parseInt(minute, 10) > 59) return raw;
    const meridiem = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute} ${meridiem}`;
  };

  const captureGiftnoteSummary = (root) => {
    const key = currentGiftCardKey();
    if (!key) return null;

    const scheduled = root.querySelector('#scheduled-tab')?.getAttribute('aria-selected') === 'true';
    return {
      key,
      recipient: root.querySelector('#to')?.value.trim() || '',
      deliveryMethod: scheduled ? 'later' : 'now',
      date: scheduled ? formatGiftCardDate(root.querySelector('#date')?.value) : '',
      time: scheduled ? formatGiftCardTime(root.querySelector('#time')?.value) : '',
    };
  };

  const markCurrentGiftCardSaved = () => {
    const key = currentGiftCardKey();
    currentGiftCardItems().forEach((item) => {
      item.dataset.giftnoteDetailsSaved = 'true';
    });
    if (key) writeSavedGiftCardKey(key);
    if (state.pendingGiftnoteSummary?.key === key) writeGiftCardSummary(state.pendingGiftnoteSummary);
    clearPendingGiftCardKey();
    state.pendingFreshGiftCard = false;
    state.awaitingGiftnoteSave = false;
    state.pendingGiftnoteSummary = null;
    state.freshFieldsResetFor = '';
    document.querySelectorAll('giftnote-button').forEach(syncGiftnoteButtonState);
  };

  const markCurrentGiftCardUnsaved = () => {
    const key = currentGiftCardKey();
    currentGiftCardItems().forEach((item) => {
      item.dataset.giftnoteDetailsSaved = 'false';
    });
    if (key) writePendingGiftCardKey(key);
    clearSavedGiftCardKey();
    clearGiftCardSummary();
    state.awaitingGiftnoteSave = false;
    state.pendingGiftnoteSummary = null;
    state.freshFieldsResetFor = '';
    document.querySelectorAll('giftnote-button').forEach(syncGiftnoteButtonState);
  };

  const clearStaleGiftnoteFields = (root, modal) => {
    const key = currentGiftCardKey();
    if (!key || !currentGiftCardNeedsDetails() || !giftnoteModalIsOpen(modal) || state.freshFieldsResetFor === key) return;

    ['#to', '#from', '#message', '#email'].forEach((selector) => {
      const field = root.querySelector(selector);
      if (!field || !field.value) return;
      field.value = '';
      field.dispatchEvent(new Event('input', {bubbles: true, composed: true}));
      field.dispatchEvent(new Event('change', {bubbles: true, composed: true}));
    });

    ['#to-field', '#from-field', '#message-field', '#email-field'].forEach((selector) => {
      root.querySelector(selector)?.classList.remove('required', 'invalid', 'error');
    });
    ['#to-required', '#to-invalid', '#to-error', '#from-required', '#from-invalid', '#from-error', '#message-required', '#message-invalid', '#message-error', '#email-invalid', '#email-error'].forEach((selector) => {
      root.querySelector(selector)?.classList.add('hidden');
    });

    const messageCount = root.querySelector('#message-count');
    if (messageCount) setText(messageCount, '0');
    state.freshFieldsResetFor = key;
  };

  const bindGiftnoteActions = (root) => {
    const saveButton = root.querySelector('#save-button');
    const removeButton = root.querySelector('#remove-button');

    if (saveButton && !state.observedGiftnoteActions.has(saveButton)) {
      saveButton.addEventListener('click', () => {
        state.awaitingGiftnoteSave = true;
        state.pendingGiftnoteSummary = captureGiftnoteSummary(root);
      }, true);
      state.observedGiftnoteActions.add(saveButton);
    }

    if (removeButton && !state.observedGiftnoteActions.has(removeButton)) {
      removeButton.addEventListener('click', markCurrentGiftCardUnsaved, true);
      state.observedGiftnoteActions.add(removeButton);
    }
  };

  const applyGiftnoteModalMode = (core) => {
    const root = core.shadowRoot;
    if (!root) return;

    let style = root.querySelector('#molten-giftnote-gift-card-styles');
    if (!style) {
      style = document.createElement('style');
      style.id = 'molten-giftnote-gift-card-styles';
      style.textContent = giftnoteGiftCardStyles;
      root.append(style);
    }

    const giftCardMode = digitalGiftCardOnly();
    const modal = root.querySelector('#giftnote-modal');
    const title = root.querySelector('#giftnote-modal-title');
    const description = root.querySelector('#giftnote-modal-description');
    const phoneParent = root.querySelector('#phone-field')?.parentElement;
    const emailParent = root.querySelector('#email-field')?.parentElement;
    const instantTab = root.querySelector('#instant-tab');
    const scheduledTab = root.querySelector('#scheduled-tab');
    const instantCopy = root.querySelector('#instant-panel p');
    const scheduledCopy = root.querySelector('#scheduled-panel p');
    const timingHeading = Array.from(root.querySelectorAll('h3')).find((node) => (
      node.textContent.trim() === 'When should we send your message?' ||
      node.dataset.moltenDefaultText === 'When should we send your message?'
    ));
    const fields = {
      to: root.querySelector('#to'),
      from: root.querySelector('#from'),
      message: root.querySelector('#message'),
      email: root.querySelector('#email'),
    };
    const saveLabels = [
      root.querySelector('#order-message'),
      root.querySelector('#voucher-message'),
      root.querySelector('#submit-button, button[type="submit"]'),
    ].filter(Boolean);
    const removeButton = root.querySelector('#remove-button span:not(.hidden)');
    const attribution = root.querySelector('#powered');
    const saveAlert = root.querySelector('#alert-save');
    const editLabel = document.querySelector('giftnote-button')?.shadowRoot?.querySelector('#edit');
    const appShowsSavedState = Boolean(editLabel && !editLabel.classList.contains('hidden'));
    const modalOpen = giftnoteModalIsOpen(modal);

    toggleClass(modal, 'molten-gift-card-mode', giftCardMode);
    toggleClass(modal, 'molten-fresh-gift-card', giftCardMode && currentGiftCardNeedsDetails());
    toggleClass(phoneParent, 'molten-gift-card-phone', giftCardMode);
    toggleClass(emailParent, 'molten-gift-card-email', giftCardMode);
    toggleClass(saveAlert, 'molten-gift-card-save-alert', giftCardMode);

    [title, timingHeading, instantTab, scheduledTab, instantCopy, scheduledCopy, ...saveLabels, removeButton].forEach(rememberText);
    Object.values(fields).forEach(rememberPlaceholder);
    rememberHtml(description);

    if (giftCardMode) {
      setText(title, 'Personalise this gift card');
      if (description && description.innerHTML !== '<p>Add the recipient, write your message and choose when the gift card will be sent.</p>') {
        description.innerHTML = '<p>Add the recipient, write your message and choose when the gift card will be sent.</p>';
      }
      setText(timingHeading, 'When would you like it sent?');
      setText(instantTab, 'Send now');
      setText(scheduledTab, 'Send later');
      setText(instantCopy, 'Sent by email after you complete your order');
      setText(scheduledCopy, 'Sent on the date and time you choose. Times shown use Brisbane time.');
      fields.to?.setAttribute('placeholder', 'Who is it for?');
      fields.from?.setAttribute('placeholder', 'Who is this from?');
      fields.message?.setAttribute('placeholder', 'Write your message');
      fields.email?.setAttribute('placeholder', 'name@example.com');
      saveLabels.forEach((node) => setText(node, 'Save gift card details'));
      setText(removeButton, 'Remove gift card details');
      if (attribution && attribution.dataset.moltenHidden !== 'true') {
        attribution.dataset.moltenHidden = 'true';
        attribution.setAttribute('aria-hidden', 'true');
        attribution.style.setProperty('display', 'none', 'important');
      }

      setGiftnoteRequiredFields(root, true);
      bindGiftnoteActions(root);
      clearStaleGiftnoteFields(root, modal);

      if (state.awaitingGiftnoteSave && !modalOpen && appShowsSavedState) {
        markCurrentGiftCardSaved();
      }

      if (root.querySelector('#tracked-tab')?.getAttribute('aria-selected') === 'true') {
        instantTab?.click();
      }
    } else {
      [title, timingHeading, instantTab, scheduledTab, instantCopy, scheduledCopy, ...saveLabels, removeButton].forEach((node) => {
        if (node?.dataset.moltenDefaultText !== undefined) setText(node, node.dataset.moltenDefaultText);
      });
      Object.values(fields).forEach((field) => {
        if (field?.dataset.moltenDefaultPlaceholder !== undefined) {
          field.setAttribute('placeholder', field.dataset.moltenDefaultPlaceholder);
        }
      });
      if (attribution?.dataset.moltenHidden === 'true') {
        attribution.style.removeProperty('display');
        attribution.removeAttribute('aria-hidden');
        delete attribution.dataset.moltenHidden;
      }
      if (description?.dataset.moltenDefaultHtml !== undefined && description.innerHTML !== description.dataset.moltenDefaultHtml) {
        description.innerHTML = description.dataset.moltenDefaultHtml;
      }
      setGiftnoteRequiredFields(root, false);
    }
  };

  const bindGiftnoteCoreObservers = () => {
    document.querySelectorAll('giftnote-core').forEach((core) => {
      if (!core.shadowRoot) return;
      applyGiftnoteModalMode(core);
      if (state.observedGiftnoteCores.has(core)) return;

      const observer = new MutationObserver(() => applyGiftnoteModalMode(core));
      observer.observe(core.shadowRoot, {attributes: true, attributeFilter: ['aria-selected', 'aria-hidden', 'class'], childList: true, subtree: true});
      state.observedGiftnoteCores.add(core);
    });
  };

  const refreshGiftnote = () => {
    state.giftnoteRefreshFrame = null;
    syncGiftCardMode();
    if (state.pendingFreshGiftCard && digitalGiftCardOnly()) {
      const key = currentGiftCardKey();
      if (key) {
        writePendingGiftCardKey(key);
        clearSavedGiftCardKey();
        clearGiftCardSummary();
        currentGiftCardItems().forEach((item) => {
          item.dataset.giftnoteDetailsSaved = 'false';
        });
        state.pendingFreshGiftCard = false;
      }
    }
    bindGiftnoteButtonObservers();
    bindGiftnoteCoreObservers();
  };

  const scheduleGiftnoteRefresh = () => {
    if (state.giftnoteRefreshFrame !== null) return;
    state.giftnoteRefreshFrame = requestAnimationFrame(refreshGiftnote);
  };

  const initialise = () => {
    syncDrawerState();
    bindErrorObservers();
    scheduleGiftnoteRefresh();
  };

  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener('change', onDocumentChange, true);
  document.addEventListener('theme:cart:loaded', initialise);
  document.addEventListener('theme:cart:added', () => requestAnimationFrame(announceSuccess));

  const giftnoteDocumentObserver = new MutationObserver(scheduleGiftnoteRefresh);
  giftnoteDocumentObserver.observe(document.documentElement, {childList: true, subtree: true});

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, {once: true});
  } else {
    initialise();
  }
})();
